import '.ac{position:relative;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.u{gap:0.5rem;}.ssr-plugin'
import '.l{width:100%;}.ssr-plugin'
import '.cq{font-size:1.125rem;}.ssr-plugin'
import '.ev{appearance:none;}.ssr-plugin'
import '.f3{height:2.75rem;}.ssr-plugin'
import '.he{padding:0.5rem 1rem;}.ssr-plugin'
import '.cp{border-radius:0.75rem;}.ssr-plugin'
import '.iz{text-align:left;}.ssr-plugin'
import '.a1{cursor:pointer;}.ssr-plugin'
import '.bw{color:var(--gray-300);}.ssr-plugin'
import '.f2{flex:1 1 auto;}.ssr-plugin'
import '.aa{min-width:0px;}.ssr-plugin'
import '.c2{overflow:hidden;}.ssr-plugin'
import '.f5{text-overflow:ellipsis;}.ssr-plugin'
import '.ei{white-space:nowrap;}.ssr-plugin'
import '.be{position:absolute;}.ssr-plugin'
import '.ke{top:calc(100% + 0.25rem);}.ssr-plugin'
import '.bg{left:0px;}.ssr-plugin'
import '.bh{right:0px;}.ssr-plugin'
import '.bd{z-index:10;}.ssr-plugin'
import '.k5{border:1px solid var(--palette-blue-50);}.ssr-plugin'
import '.e1{background:var(--palette-white);}.ssr-plugin'
import '.hc{box-shadow:0 3px 10px 0 rgba(0, 0, 0, 0.05);}.ssr-plugin'
import '.d6{max-height:16rem;}.ssr-plugin'
import '.br{overflow-y:auto;}.ssr-plugin'
import { faChevronDown, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useRef, useState } from 'react'
import { Style, styled } from 'inlines'
import { Checkbox } from '@app/components/core/checkbox/Checkbox'
import { Tag } from '@app/components/core/tag/Tag'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Text } from '@app/components/core/typography/Text'
import { InputField } from '../InputField'

export type MultiSelectOption<T extends string | number> = {
  value: T
  label: string
}

export type MultiSelectProps<T extends string | number> = {
  label?: string
  labelStyle?: Style
  subTitle?: string
  options: MultiSelectOption<T>[]
  selectedValues: T[]
  onToggle: (value: T) => void
  notSelectedLabel?: string
  variant?: 'outlined' | 'muted-blue'
}

export const MultiSelect = <T extends string | number>({
  label,
  labelStyle,
  subTitle,
  options,
  selectedValues,
  onToggle,
  notSelectedLabel,
  variant = 'outlined',
}: MultiSelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return
      }
      setIsOpen(false)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.value),
  )

  const hasSelection = selectedOptions.length > 0

  return (
    <InputField label={label} labelStyle={labelStyle}>
      <Column gap="0.25rem" alignItems="stretch">
        {subTitle && (
          <Text.Small color="var(--palette-blue-400)">{subTitle}</Text.Small>
        )}
        <Column gap="0.5rem" alignItems="stretch">
          <div className="ac d e"
            ref={containerRef}
          >
            <button className="d t u l cq ev f3 he cp iz a1 bw"
              type="button"
              onClick={() => setIsOpen((open) => !open)}
              style={{
                border: `1px solid ${variant === 'outlined' ? 'var(--palette-blue-300)' : 'var(--palette-blue-50)'}`,
                background:
                  variant === 'outlined'
                    ? 'var(--palette-white)'
                    : 'var(--palette-blue-50)',}}
            >
              <span className="f2 aa c2 f5 ei"
              >
                {notSelectedLabel ?? ''}
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                color={
                  variant === 'outlined'
                    ? 'var(--palette-blue-200)'
                    : 'var(--brand-secondary)'
                }
                style={{ flexShrink: 0 }}
              />
            </button>
            {isOpen && (
              <div className="be ke bg bh bd cp k5 e1 hc d6 br"
              >
                <Column
                  gap="0.5rem"
                  alignItems="stretch"
                  style={{ padding: '0.75rem' }}
                >
                  {options.map((option) => (
                    <styled.div className="l"
                      key={String(option.value)}
                      style={{
                        '& label > div': {
                          width: '100%',
                          justifyContent: 'space-between',
                        },
                      }}
                    >
                      <Checkbox
                        label={option.label}
                        labelSize="small"
                        labelPosition="left"
                        selected={selectedValues.includes(option.value)}
                        onClick={() => onToggle(option.value)}
                      />
                    </styled.div>
                  ))}
                </Column>
              </div>
            )}
          </div>
          {hasSelection && (
            <Row
              style={{
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              {selectedOptions.map((option) => (
                <Tag
                  key={String(option.value)}
                  variant="transparent"
                  size="small"
                  label={option.label}
                  iconRight={faXmark}
                  onClick={() => onToggle(option.value)}
                />
              ))}
            </Row>
          )}
        </Column>
      </Column>
    </InputField>
  )
}
